import {
  useState,
  useCallback,
  useEffect,
  MutableRefObject,
  useLayoutEffect,
} from 'react';

interface UseDomChartZoomScrollOptions {
  zoomMultiplier?: number;
  minZoom?: number;
  maxZoom?: number;
  chartRef: MutableRefObject<HTMLDivElement | null>;
}

const DEFAULT_ZOOM_MULTIPLIER = 1.2;
const DEFAULT_MIN_ZOOM = 1;
const DEFAULT_MAX_ZOOM = 100;
const LAYOUT_OFFSET = 32;

export const useDomChartZoomScroll = ({
  chartRef,
  zoomMultiplier = DEFAULT_ZOOM_MULTIPLIER,
  minZoom = DEFAULT_MIN_ZOOM,
  maxZoom = DEFAULT_MAX_ZOOM,
}: UseDomChartZoomScrollOptions) => {
  const [zoom, setZoom] = useState(1);
  const [initialZoom, setInitialZoom] = useState(1);
  const [selectStartX, setSelectStartX] = useState<number | null>(null);
  const [selectCurrentX, setSelectCurrentX] = useState<number | null>(null);

  const handleZoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev * zoomMultiplier, maxZoom));
  }, [zoomMultiplier, maxZoom]);

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => Math.max(prev / zoomMultiplier, minZoom));
  }, [zoomMultiplier, minZoom]);

  const setZoomManually = useCallback(
    (value: number, updateInitial = false) => {
      const clamped = Math.min(Math.max(value, minZoom), maxZoom);
      setZoom(clamped);

      if (updateInitial) {
        setInitialZoom(clamped);
      }
    },
    [minZoom, maxZoom],
  );

  const resetZoom = useCallback(() => {
    setZoom(initialZoom);

    requestAnimationFrame(() => {
      const chart = chartRef.current;

      if (chart) {
        chart.scrollTo({ left: chart.scrollWidth });
      }
    });
  }, [chartRef, initialZoom]);

  const handleScrollLeft = useCallback(
    (ref: React.RefObject<HTMLDivElement>) => {
      ref.current?.scrollBy({
        left: -ref.current.clientWidth * 0.2,
        behavior: 'smooth',
      });
    },
    [],
  );

  const handleScrollRight = useCallback(
    (ref: React.RefObject<HTMLDivElement>) => {
      ref.current?.scrollBy({
        left: ref.current.clientWidth * 0.2,
        behavior: 'smooth',
      });
    },
    [],
  );

  const scrollToPercent = useCallback(
    (ref: React.RefObject<HTMLDivElement>, percent: number) => {
      const el = ref.current;
      if (!el) return;
      const scrollLeft = percent * el.scrollWidth - el.clientWidth;
      el.scrollTo({ left: scrollLeft });
    },
    [],
  );

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const chart = e.currentTarget;
    const { clientHeight, offsetHeight } = chart;

    const scrollbarHeight = offsetHeight - clientHeight;
    const clickY = e.clientY - chart.getBoundingClientRect().top;

    // If clicked in the scrollbar area, skip selection
    if (scrollbarHeight > 0 && clickY > clientHeight) {
      return;
    }

    const chartLeft = e.currentTarget.getBoundingClientRect().left;
    setSelectStartX(e.clientX - chartLeft + LAYOUT_OFFSET);
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (selectStartX != null) {
        const chartLeft = e.currentTarget.getBoundingClientRect().left;
        setSelectCurrentX(e.clientX - chartLeft + LAYOUT_OFFSET);
      }
    },
    [selectStartX],
  );

  const handleMouseUp = () => {
    const chart = chartRef.current;
    if (!chart || selectStartX == null || selectCurrentX == null) return;

    const chartWidth = chart.clientWidth;
    const chartScrollWidthBefore = chart.scrollWidth;

    const start = Math.min(selectStartX, selectCurrentX);
    const end = Math.max(selectStartX, selectCurrentX);
    const selWidth = end - start;
    const ratio = selWidth / chartWidth;

    if (ratio < 0.01) {
      setSelectStartX(null);
      setSelectCurrentX(null);
      return;
    }

    const newZoom = zoom / ratio;
    const zoomed = Math.min(newZoom, maxZoom);

    const center = start + selWidth / 2;
    const centerAbsolute = chart.scrollLeft + center;
    const scrollPercent = centerAbsolute / chartScrollWidthBefore;

    setZoom(zoomed);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const c2 = chartRef.current;
        if (!c2) return;

        const scrollLeft = scrollPercent * c2.scrollWidth - c2.clientWidth / 2;

        c2.scrollTo({ left: Math.max(0, scrollLeft) });
      });
    });

    setSelectStartX(null);
    setSelectCurrentX(null);
  };

  const isMaxZoom = zoom >= maxZoom;
  const isMinZoom = zoom <= minZoom;
  const isInitialZoom = initialZoom.toFixed(1) === zoom.toFixed(1);

  useEffect(() => {
    const clearSelection = () => {
      setSelectStartX(null);
      setSelectCurrentX(null);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Cancel selection and stop event from reaching parent (e.g., drawer)
        e.stopPropagation();
        e.preventDefault();

        clearSelection();
      }
    };

    const handleMouseMoveOutside = (e: MouseEvent) => {
      if (selectStartX == null || !chartRef.current) return;

      const bounds = chartRef.current.getBoundingClientRect();

      const { clientX } = e;

      if (clientX < bounds.left) {
        // Snap to start (far left)
        setSelectCurrentX(0 + LAYOUT_OFFSET);
        return;
      }

      if (clientX > bounds.right) {
        // Snap to end (far right)
        setSelectCurrentX(bounds.width + LAYOUT_OFFSET);
        return;
      }

      // If still within horizontal bounds, clamp normally
      const relativeX = clientX - bounds.left;
      setSelectCurrentX(relativeX + LAYOUT_OFFSET);
    };

    if (selectStartX !== null) {
      window.addEventListener('keydown', handleKeyDown, true); // useCapture = true
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('mousemove', handleMouseMoveOutside);
    } else {
      window.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('keydown', handleMouseUp, true);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleMouseMoveOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectStartX, selectCurrentX]);

  useLayoutEffect(() => {
    chartRef.current?.scrollTo({ left: chartRef.current?.scrollWidth });
  }, [chartRef, initialZoom]);

  return {
    zoom,
    handleZoomIn,
    handleZoomOut,
    setZoomManually,
    resetZoom,
    handleScrollLeft,
    handleScrollRight,
    scrollToPercent,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    selectStartX,
    selectCurrentX,
    isMaxZoom,
    isMinZoom,
    isInitialZoom,
  };
};
